import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import arsClaimService from '../../../services/arsClaimService';
import { patientService } from '../../../services/patientService';
import { patientInsuranceService, insuranceCompanyService, insurancePlanService } from '../../../services/insuranceService';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatCurrency } from '../../../lib/format';

interface LineItem {
    key: string;
    description: string;
    quantity: number;
    unit_price: number;
    claimable_type?: string;
    claimable_id?: number;
}

interface PatientResult {
    id: number;
    first_name: string;
    last_name: string;
    identification: string;
}

interface InsuranceInfo {
    companyId: number;
    companyName: string;
    planId: number;
    planName: string;
}

const ClaimFormPage: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // Step 1: Patient + Insurance
    const [patientSearch, setPatientSearch] = useState('');
    const [patients, setPatients] = useState<PatientResult[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<PatientResult | null>(null);
    const [patientInsurances, setPatientInsurances] = useState<Array<{ id: number; insurance_company_plan_id: number; company?: { id: number; name: string } | null; plan?: { id: number; name: string } | null }>>([]);
    const [selectedInsurance, setSelectedInsurance] = useState<{ companyId: number; planId: number } | null>(null);

    // Step 2: Items
    const [items, setItems] = useState<LineItem[]>([
        { key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0 },
    ]);

    // Step 3: Notes
    const [notes, setNotes] = useState('');

    const searchPatients = useCallback(async (q: string) => {
        if (!q || q.length < 2) {
            setPatients([]);
            return;
        }
        try {
            const res = await patientService.search(q);
            setPatients((res || []) as unknown as PatientResult[]);
        } catch {
            setPatients([]);
        }
    }, []);

    useEffect(() => {
        if (selectedPatient) return;
        const timer = setTimeout(() => searchPatients(patientSearch), 300);
        return () => clearTimeout(timer);
    }, [patientSearch, searchPatients, selectedPatient]);

    const handleSelectPatient = async (patient: PatientResult) => {
        setSelectedPatient(patient);
        setPatientSearch(`${patient.first_name} ${patient.last_name}`);
        setPatients([]);

        try {
            const res = await patientInsuranceService.getByPatient(patient.id);
            setPatientInsurances(res);
            if (res.length > 0) {
                const first = res[0];
                setSelectedInsurance({
                    companyId: first.company?.id ?? 0,
                    planId: first.plan?.id ?? 0,
                });
            }
        } catch {
            setPatientInsurances([]);
        }
    };

    const addItem = () => {
        setItems(prev => [...prev, { key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0 }]);
    };

    const removeItem = (key: string) => {
        setItems(prev => prev.filter(i => i.key !== key));
    };

    const updateItem = (key: string, field: keyof LineItem, value: string | number) => {
        setItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    };

    const handleSubmit = async () => {
        setFormError(null);
        if (!selectedPatient || !selectedInsurance) {
            setFormError('Debe seleccionar paciente y seguro');
            return;
        }

        const validItems = items.filter(i => i.description && i.quantity > 0 && i.unit_price > 0);
        if (validItems.length === 0) {
            setFormError('Debe agregar al menos un item válido');
            return;
        }

        setSubmitting(true);
        try {
            const res = await arsClaimService.create({
                patient_id: selectedPatient.id,
                insurance_company_id: selectedInsurance.companyId,
                insurance_plan_id: selectedInsurance.planId,
                items: validItems.map(i => ({
                    description: i.description,
                    quantity: i.quantity,
                    unit_price: i.unit_price,
                    claimable_type: i.claimable_type,
                    claimable_id: i.claimable_id,
                })),
                notes: notes || undefined,
            });
            navigate(`/claims/${res.data.id}`);
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
            const message = axiosErr?.response?.data?.message || 'Error al crear reclamación';
            const errors = axiosErr?.response?.data?.errors;
            if (errors) {
                setFormError(`${message}: ${Object.values(errors).flat().join(', ')}`);
            } else {
                setFormError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const total = calculateTotal();

    return (
        <>
            <div className="page-header">
                <div className="row align-items-center">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Reclamaciones', to: '/claims' }, { label: 'Nueva Reclamación' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            {formError && (
                                <div className="alert alert-danger">
                                    <i className="fa fa-exclamation-circle me-2" />
                                    {formError}
                                </div>
                            )}

                            {/* Steps indicator */}
                            <div className="mb-4">
                                <ul className="nav nav-pills nav-justified">
                                    <li className="nav-item">
                                        <span className={`nav-link ${step >= 1 ? 'active' : 'disabled'}`}>
                                            1. Paciente & Seguro
                                        </span>
                                    </li>
                                    <li className="nav-item">
                                        <span className={`nav-link ${step >= 2 ? 'active' : 'disabled'}`}>
                                            2. Items
                                        </span>
                                    </li>
                                    <li className="nav-item">
                                        <span className={`nav-link ${step >= 3 ? 'active' : 'disabled'}`}>
                                            3. Revisar & Confirmar
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* Step 1: Patient & Insurance */}
                            {step === 1 && (
                                <div>
                                    <h5>Seleccionar Paciente</h5>
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar paciente por nombre o cédula..."
                                            value={patientSearch}
                                            onChange={e => {
                                                setPatientSearch(e.target.value);
                                                if (selectedPatient) setSelectedPatient(null);
                                            }}
                                        />
                                        {patients.length > 0 && !selectedPatient && (
                                            <ul className="list-group mt-1" style={{ maxHeight: 200, overflow: 'auto' }}>
                                                {patients.map(p => (
                                                    <li
                                                        key={p.id}
                                                        className="list-group-item list-group-item-action"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSelectPatient(p)}
                                                    >
                                                        {p.first_name} {p.last_name} - {p.identification}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {selectedPatient && (
                                        <div className="alert alert-info">
                                            <strong>Paciente seleccionado:</strong> {selectedPatient.first_name} {selectedPatient.last_name}
                                        </div>
                                    )}

                                    {patientInsurances.length > 0 && (
                                        <div className="mb-3">
                                            <h5>Seleccionar Seguro</h5>
                                            <select
                                                className="form-select"
                                                value={selectedInsurance ? `${selectedInsurance.companyId}-${selectedInsurance.planId}` : ''}
                                                onChange={e => {
                                                    const [companyId, planId] = e.target.value.split('-').map(Number);
                                                    setSelectedInsurance({ companyId, planId });
                                                }}
                                            >
                                                {patientInsurances.map(pi => (
                                                    <option key={pi.id} value={`${pi.company?.id}-${pi.plan?.id}`}>
                                                        {pi.company?.name} - {pi.plan?.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div className="text-end">
                                        <button
                                            className="btn btn-primary"
                                            disabled={!selectedPatient || !selectedInsurance}
                                            onClick={() => setStep(2)}
                                        >
                                            Continuar <i className="fa fa-arrow-right ms-1" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Items */}
                            {step === 2 && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Items de la Reclamación</h5>
                                        <button className="btn btn-sm btn-outline-primary" onClick={addItem}>
                                            <i className="fa fa-plus me-1" /> Agregar Item
                                        </button>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '40%' }}>Descripción</th>
                                                    <th style={{ width: '15%' }}>Cantidad</th>
                                                    <th style={{ width: '20%' }}>Precio Unit.</th>
                                                    <th style={{ width: '15%' }}>Subtotal</th>
                                                    <th style={{ width: '10%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map(item => (
                                                    <tr key={item.key}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Descripción del item"
                                                                value={item.description}
                                                                onChange={e => updateItem(item.key, 'description', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                min="0.01"
                                                                step="0.01"
                                                                value={item.quantity}
                                                                onChange={e => updateItem(item.key, 'quantity', parseFloat(e.target.value) || 0)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.unit_price}
                                                                onChange={e => updateItem(item.key, 'unit_price', parseFloat(e.target.value) || 0)}
                                                            />
                                                        </td>
                                                        <td className="text-end">
                                                            {formatCurrency(item.quantity * item.unit_price)}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => removeItem(item.key)}
                                                                disabled={items.length <= 1}
                                                                aria-label="Eliminar ítem"
                                                            >
                                                                <i className="fa fa-trash" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={3} className="text-end"><strong>Total:</strong></td>
                                                    <td className="text-end">
                                                        <strong>{formatCurrency(total)}</strong>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-between">
                                        <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                                            <i className="fa fa-arrow-left me-1" /> Atrás
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => setStep(3)}
                                            disabled={items.filter(i => i.description && i.quantity > 0 && i.unit_price > 0).length === 0}
                                        >
                                            Revisar <i className="fa fa-arrow-right ms-1" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Review & Confirm */}
                            {step === 3 && (
                                <div>
                                    <h5>Revisar Reclamación</h5>

                                    <div className="mb-3">
                                        <strong>Paciente:</strong> {selectedPatient?.first_name} {selectedPatient?.last_name}
                                        <br />
                                        <strong>Seguro:</strong> {patientInsurances.find(pi => pi.company?.id === selectedInsurance?.companyId)?.company?.name ?? '-'}
                                        {' - '}
                                        {patientInsurances.find(pi => pi.plan?.id === selectedInsurance?.planId)?.plan?.name ?? '-'}
                                    </div>

                                    <div className="table-responsive mb-3">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Descripción</th>
                                                    <th>Cant.</th>
                                                    <th>Precio</th>
                                                    <th>Subtotal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.filter(i => i.description && i.quantity > 0 && i.unit_price > 0).map(item => (
                                                    <tr key={item.key}>
                                                        <td>{item.description}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{formatCurrency(item.unit_price)}</td>
                                                        <td>{formatCurrency(item.quantity * item.unit_price)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={3} className="text-end"><strong>Total:</strong></td>
                                                    <td><strong>{formatCurrency(total)}</strong></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Notas (opcional)</label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={notes}
                                            onChange={e => setNotes(e.target.value)}
                                            placeholder="Notas adicionales..."
                                        />
                                    </div>

                                    <div className="d-flex justify-content-between">
                                        <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>
                                            <i className="fa fa-arrow-left me-1" /> Atrás
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <><span className="spinner-border spinner-border-sm me-1" /> Creando...</>
                                            ) : (
                                                <><i className="fa fa-check me-1" /> Crear Reclamación</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClaimFormPage;
